import {KnowledgeRagTopic} from 'common/src/modules/ai/ai.enums';
import {ChatCompletionMessageParam} from 'openai/resources/chat/completions';
import {z} from 'zod';
import {CopilotWidget} from '../bootstrap/bootstrap.types';
import {AgentName, ChatStatus, ChatType, CopilotFileContentType} from '../copilot.enums';
import {ChatItem} from '../copilot.types';
import {
  PlanCollapseState,
  PlanStatus,
  PlanStepStatus,
  UserInteractionStatus,
} from '../plan/plan.enums';
import {SkillName} from '../skills/skills.enums';
import {snapshotSchemaVersion} from './persistence.consts';

const persistedFactSchema = z.object({
  what: z.string(),
  result: z.string(),
  entities: z.array(z.string()),
});

const persistedCopilotFileContentSchema = z.object({
  type: z.nativeEnum(CopilotFileContentType),
  description: z.string(),
});

const persistedCopilotFileSchema = z.object({
  id: z.string(),
  fileKey: z.string(),
  name: z.string(),
  extension: z.string(),
  content: persistedCopilotFileContentSchema.optional(),
});

const persistedAgentSessionSchema = z.object({
  sessionId: z.string(),
  memory: z.array(z.custom<ChatCompletionMessageParam>()),
  summary: z.string().nullable(),
  createdAtTimestamp: z.number(),
});

const persistedChatHistoryEntrySchema = z.object({
  sessionStartedAt: z.string(),
  sessionEndedAt: z.string(),
  sessionId: z.string(),
  agentName: z.string(),
  facts: z.array(persistedFactSchema),
});

const persistedPlanStepSchema = z.object({
  id: z.string(),
  status: z.nativeEnum(PlanStepStatus),
  title: z.string(),
  description: z.string(),
  implementationNotes: z.string(),
  issues: z.string(),
  progressNotes: z.array(z.string()),
  attemptCount: z.number(),
});

const persistedKnowledgeRAGResourceSchema = z.object({
  key: z.string(),
  title: z.string(),
  url: z.string(),
  topic: z.nativeEnum(KnowledgeRagTopic),
  content: z.string(),
  relevancyScore: z.number(),
});

const persistedPlanSchema = z.object({
  id: z.string(),
  createdAt: z.string(),
  ownerAgentName: z.nativeEnum(AgentName),
  goal: z.string(),
  steps: z.array(persistedPlanStepSchema),
  skillNames: z.array(z.nativeEnum(SkillName)),
  resources: z.array(persistedKnowledgeRAGResourceSchema).nullable(),
  issues: z.array(z.string()),
  openQuestions: z.array(z.string()),
  userInteractionStatus: z.nativeEnum(UserInteractionStatus),
});

const persistedBlackboardSchema = z.object({
  chatHistory: z.array(persistedChatHistoryEntrySchema),
  plan: persistedPlanSchema.nullable(),
});

const persistedRuntimeStateSchema = z.object({
  agentSession: persistedAgentSessionSchema,
  blackboard: persistedBlackboardSchema,
  files: z.array(persistedCopilotFileSchema),
});

const persistedPlanStateSchema = persistedPlanSchema.extend({
  status: z.nativeEnum(PlanStatus),
});

const persistedReduxStateSchema = z.object({
  type: z.nativeEnum(ChatType),
  title: z.string(),
  isPlanModeEnabled: z.boolean(),
  chatItems: z.array(z.custom<ChatItem>()),
  modalWidget: z.custom<CopilotWidget | null>(),
  blockingChatItemId: z.string().nullable(),
  informationText: z.string(),
  draftMessage: z.object({text: z.string(), fileIds: z.array(z.string())}),
  status: z.nativeEnum(ChatStatus),
  plan: persistedPlanStateSchema.nullable(),
  isPlanModeToggleOn: z.boolean(),
  planCollapseState: z.nativeEnum(PlanCollapseState),
  contextWindowUsage: z.number(),
  lastUpdated: z.string(),
});

export const persistedChatSnapshotSchema = z.object({
  version: z.number().refine(
    (version) => version === snapshotSchemaVersion,
    (version) => ({
      message: `Snapshot version ${version} is not supported. Expected version ${snapshotSchemaVersion}.`,
    }),
  ),
  createdAt: z.string(),
  chatId: z.string(),
  chatType: z.nativeEnum(ChatType),
  userLocationUrlAtExport: z.string().optional(),
  redux: persistedReduxStateSchema,
  runtime: persistedRuntimeStateSchema,
});
